/**
 * Password strength scoring.
 *
 * Returns a score from 0 (empty) to 4 (very strong) plus a human label
 * and a brand-aligned color token to render in the UI.
 */

export type PasswordStrengthLabel = "empty" | "weak" | "medium" | "strong";

export type PasswordStrength = {
    score: 0 | 1 | 2 | 3 | 4;
    label: PasswordStrengthLabel;
    display: string;
    /** Tailwind class used for the filled meter segments. */
    color: string;
    /** Foreground text color for the label. */
    textColor: string;
};

function countChecks(password: string): number {
    if (!password) return 0;
    let checks = 0;
    if (password.length >= 8) checks += 1;
    if (password.length >= 12) checks += 1;
    if (/[A-Z]/.test(password) && /[a-z]/.test(password)) checks += 1;
    if (/[0-9]/.test(password)) checks += 1;
    if (/[^A-Za-z0-9]/.test(password)) checks += 1;
    return checks;
}

export function evaluatePassword(password: string): PasswordStrength {
    if (!password) {
        return {
            score: 0,
            label: "empty",
            display: "—",
            color: "bg-border",
            textColor: "text-muted-foreground",
        };
    }
    const checks = countChecks(password);
    if (checks <= 2) {
        return {
            score: 1,
            label: "weak",
            display: "Weak",
            color: "bg-error",
            textColor: "text-error",
        };
    }
    if (checks === 3) {
        return {
            score: 2,
            label: "medium",
            display: "Medium",
            color: "bg-accentbrand-500",
            textColor: "text-accentbrand-700",
        };
    }
    if (checks === 4) {
        return {
            score: 3,
            label: "strong",
            display: "Strong",
            color: "bg-brand",
            textColor: "text-brand",
        };
    }
    return {
        score: 4,
        label: "strong",
        display: "Very strong",
        color: "bg-brand",
        textColor: "text-brand",
    };
}
